/**
 * Purchases - goods in from a supplier.
 *
 * Posting one moves stock and re-averages the cost of every item on it, which
 * is what keeps the margin figures on the recipe screen honest.
 *
 * Quantities are typed in the delivery unit ("4 cans"), not the recipe unit
 * ("20000 ml"), because that is what the delivery note says. The conversion
 * happens on the server.
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiTruck, FiTrash2, FiUsers, FiBox } from 'react-icons/fi';
import toast from 'react-hot-toast';

import { inventoryApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, Badge } from '../components/ui/Feedback';
import { Field, Input, NumberInput, Select, Textarea } from '../components/ui/Field';
import { money, quantity, formatDate, toInputDate } from '../utils/format';

const PurchasesPage = () => {
    const [filters, setFilters] = useState({
        fromDate: toInputDate(new Date(Date.now() - 30 * 86400000)),
        toDate: toInputDate(new Date()),
        page: 1,
        pageSize: 25
    });
    const [creating, setCreating] = useState(false);
    const [viewing, setViewing] = useState(null);

    const load = useCallback(() => inventoryApi.listPurchases(filters), [filters]);
    const { data: purchases, meta, loading, refetch } = useAsync(load, [filters]);

    const loadSuppliers = useCallback(() => inventoryApi.listSuppliers(), []);
    const { data: suppliers, refetch: refetchSuppliers } = useAsync(loadSuppliers, []);

    const loadStock = useCallback(() => inventoryApi.listItems({}), []);
    const { data: stockItems } = useAsync(loadStock, []);

    const columns = [
        { key: 'Purchase_Number', header: 'Number' },
        {
            key: 'Purchase_Date',
            header: 'Date',
            render: (row) => formatDate(row.Purchase_Date)
        },
        { key: 'Supplier_Name', header: 'Supplier' },
        {
            key: 'Supplier_Invoice_No',
            header: 'Their invoice',
            render: (row) => row.Supplier_Invoice_No || '—'
        },
        { key: 'Item_Count', header: 'Lines', align: 'right' },
        {
            key: 'Total_Amount',
            header: 'Total',
            align: 'right',
            render: (row) => <span className="font-medium">{money(row.Total_Amount)}</span>
        },
        {
            key: 'balance',
            header: 'Owed',
            align: 'right',
            render: (row) => {
                const owed = Number(row.Total_Amount) - Number(row.Paid_Amount);
                return owed > 0
                    ? <span className="font-medium text-amber-700">{money(owed)}</span>
                    : <Badge tone="green">paid</Badge>;
            }
        },
        {
            key: 'Is_Posted',
            header: '',
            render: (row) => (row.Is_Posted
                ? <Badge tone="green">stock updated</Badge>
                : <Badge tone="amber">draft</Badge>)
        }
    ];

    return (
        <div className="page">
            <PageHeader
                title="Purchases"
                subtitle="Deliveries in, and what they cost"
                actions={(
                    <>
                        {/* The two lists this screen's pickers are built from. */}
                        <Link to="/suppliers" className="btn-secondary">
                            <FiUsers className="h-4 w-4" /> Suppliers
                        </Link>
                        <Link to="/inventory" className="btn-secondary">
                            <FiBox className="h-4 w-4" /> Stock items
                        </Link>
                        <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
                            <FiPlus className="h-4 w-4" /> Record a delivery
                        </button>
                    </>
                )}
            />

            <div className="flex items-center gap-2">
                <input
                    type="date"
                    className="input w-full min-w-0 flex-1 sm:w-40 sm:flex-none"
                    value={filters.fromDate}
                    onChange={(event) => setFilters({ ...filters, fromDate: event.target.value, page: 1 })}
                />
                <input
                    type="date"
                    className="input w-full min-w-0 flex-1 sm:w-40 sm:flex-none"
                    value={filters.toDate}
                    onChange={(event) => setFilters({ ...filters, toDate: event.target.value, page: 1 })}
                />
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={purchases || []}
                loading={loading}
                meta={meta}
                onPageChange={(page) => setFilters({ ...filters, page })}
                rowKey={(row) => row.Purchase_ID}
                onRowClick={async (row) => {
                    try {
                        setViewing(await inventoryApi.getPurchase(row.Purchase_ID));
                    } catch (err) {
                        toast.error(err.message);
                    }
                }}
                emptyTitle="No purchases in this range"
                emptyIcon={FiTruck}
            />

            <PurchaseForm
                open={creating}
                suppliers={suppliers || []}
                stockItems={stockItems || []}
                onClose={() => setCreating(false)}
                onSaved={() => { refetch(); refetchSuppliers(); }}
            />

            {/* One purchase, in full */}
            <Modal
                open={!!viewing}
                onClose={() => setViewing(null)}
                title={viewing?.Purchase_Number || ''}
                subtitle={viewing ? `${viewing.Supplier_Name} · ${formatDate(viewing.Purchase_Date)}` : ''}
                width="lg"
            >
                {viewing && (
                    <>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th className="text-right">Qty</th>
                                    <th className="text-right">Rate</th>
                                    <th className="text-right">Into stock</th>
                                    <th className="text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewing.items.map((item) => (
                                    <tr key={item.Purchase_Item_ID}>
                                        <td>{item.Stock_Item_Name}</td>
                                        <td className="tabular text-right">
                                            {quantity(item.Quantity)} {item.UOM_Description}
                                        </td>
                                        <td className="tabular text-right">{money(item.Unit_Price)}</td>
                                        <td className="tabular text-right text-slate-500">
                                            {quantity(item.Stock_Quantity)}
                                        </td>
                                        <td className="tabular text-right font-medium">
                                            {money(item.Line_Total)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <dl className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-slate-600">Sub total</dt>
                                <dd className="tabular">{money(viewing.Sub_Total)}</dd>
                            </div>
                            {Number(viewing.Discount_Amount) > 0 && (
                                <div className="flex justify-between">
                                    <dt className="text-slate-600">Discount</dt>
                                    <dd className="tabular">−{money(viewing.Discount_Amount)}</dd>
                                </div>
                            )}
                            {Number(viewing.Tax_Amount) > 0 && (
                                <div className="flex justify-between">
                                    <dt className="text-slate-600">Tax</dt>
                                    <dd className="tabular">{money(viewing.Tax_Amount)}</dd>
                                </div>
                            )}
                            {Number(viewing.Freight_Amount) > 0 && (
                                <div className="flex justify-between">
                                    <dt className="text-slate-600">Freight</dt>
                                    <dd className="tabular">{money(viewing.Freight_Amount)}</dd>
                                </div>
                            )}
                            <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold">
                                <dt>Total</dt>
                                <dd className="tabular">{money(viewing.Total_Amount)}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-slate-600">Paid</dt>
                                <dd className="tabular">{money(viewing.Paid_Amount)}</dd>
                            </div>
                        </dl>
                    </>
                )}
            </Modal>
        </div>
    );
};

/* ------------------------------- the form -------------------------------- */

const PurchaseForm = ({ open, suppliers, stockItems, onClose, onSaved }) => {
    const [form, setForm] = useState({
        supplierId: '',
        purchaseDate: toInputDate(new Date()),
        supplierInvoiceNo: '',
        freightAmount: 0,
        paidAmount: 0,
        remarks: '',
        items: [{ stockItemId: '', quantity: '', unitPrice: '', taxPercent: 0 }]
    });

    const setItem = (index, patch) => setForm((current) => ({
        ...current,
        items: current.items.map((item, i) => (i === index ? { ...item, ...patch } : item))
    }));

    const subTotal = form.items.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
        0
    );
    const taxTotal = form.items.reduce((sum, item) => {
        const line = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
        return sum + line * ((Number(item.taxPercent) || 0) / 100);
    }, 0);
    const total = subTotal + taxTotal + (Number(form.freightAmount) || 0);

    const { run: save, busy } = useAction(
        () => inventoryApi.createPurchase({
            supplierId: Number(form.supplierId),
            purchaseDate: form.purchaseDate,
            supplierInvoiceNo: form.supplierInvoiceNo || null,
            freightAmount: Number(form.freightAmount) || 0,
            paidAmount: Number(form.paidAmount) || 0,
            remarks: form.remarks || null,
            items: form.items
                .filter((item) => item.stockItemId && Number(item.quantity) > 0)
                .map((item) => ({
                    stockItemId: Number(item.stockItemId),
                    quantity: Number(item.quantity),
                    unitPrice: Number(item.unitPrice) || 0,
                    taxPercent: Number(item.taxPercent) || 0
                }))
        }),
        {
            successMessage: 'Delivery recorded and stock updated.',
            onSuccess: () => {
                setForm({
                    supplierId: '', purchaseDate: toInputDate(new Date()), supplierInvoiceNo: '',
                    freightAmount: 0, paidAmount: 0, remarks: '',
                    items: [{ stockItemId: '', quantity: '', unitPrice: '', taxPercent: 0 }]
                });
                onSaved();
                onClose();
            }
        }
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Record a delivery"
            subtitle="Enter it as the delivery note reads. Stock is updated when you save."
            width="xl"
            dismissible={!busy}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => save()}
                        disabled={busy || !form.supplierId || !form.items.some((i) => i.stockItemId)}
                    >
                        {busy ? 'Posting…' : `Post · Rs. ${money(total)}`}
                    </button>
                </>
            )}
        >
            <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Supplier" required>
                        <Select
                            value={form.supplierId}
                            onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
                        >
                            <option value="">Choose…</option>
                            {suppliers.map((s) => (
                                <option key={s.Supplier_ID} value={s.Supplier_ID}>{s.Supplier_Name}</option>
                            ))}
                        </Select>
                        {!suppliers.length && (
                            <p className="mt-1 text-xs text-slate-500">
                                None yet - add them on the{' '}
                                <Link to="/suppliers" className="font-medium text-brand-700 underline">
                                    Suppliers
                                </Link>{' '}screen.
                            </p>
                        )}
                    </Field>

                    <Field label="Date" required>
                        <Input
                            type="date"
                            value={form.purchaseDate}
                            onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
                        />
                    </Field>

                    <Field label="Invoice number of suppiler">
                        <Input
                            value={form.supplierInvoiceNo}
                            onChange={(e) => setForm({ ...form, supplierInvoiceNo: e.target.value })}
                        />
                    </Field>
                </div>

                <div className="space-y-2">
                    {form.items.map((item, index) => {
                        const stock = stockItems.find(
                            (s) => String(s.Stock_Item_ID) === String(item.stockItemId)
                        );

                        return (
                            <div key={index} className="flex items-end gap-2">
                                <Field label={index === 0 ? 'Item' : null} className="flex-1">
                                    <Select
                                        value={item.stockItemId}
                                        onChange={(e) => setItem(index, { stockItemId: e.target.value })}
                                    >
                                        <option value="">Choose…</option>
                                        {stockItems.map((s) => (
                                            <option key={s.Stock_Item_ID} value={s.Stock_Item_ID}>
                                                {s.Stock_Item_Name}
                                            </option>
                                        ))}
                                    </Select>
                                    {index === 0 && !stockItems.length && (
                                        <p className="mt-1 text-xs text-slate-500">
                                            None yet - add them on the{' '}
                                            <Link to="/inventory" className="font-medium text-brand-700 underline">
                                                Stock items
                                            </Link>{' '}screen.
                                        </p>
                                    )}
                                </Field>

                                <Field
                                    label={index === 0 ? 'Quantity' : null}
                                    className="w-28"
                                >
                                    <NumberInput
                                        value={item.quantity}
                                        onChange={(e) => setItem(index, { quantity: e.target.value })}
                                    />
                                </Field>

                                <div className="w-16 pb-2.5 text-xs text-slate-500">
                                    {stock?.Purchase_Unit || stock?.Stock_Unit || ''}
                                </div>

                                <Field label={index === 0 ? 'Rate' : null} className="w-28">
                                    <NumberInput
                                        value={item.unitPrice}
                                        onChange={(e) => setItem(index, { unitPrice: e.target.value })}
                                    />
                                </Field>

                                <Field label={index === 0 ? 'Tax %' : null} className="w-20">
                                    <NumberInput
                                        value={item.taxPercent}
                                        onChange={(e) => setItem(index, { taxPercent: e.target.value })}
                                    />
                                </Field>

                                <div className="w-24 pb-2.5 text-right text-sm font-medium">
                                    {money((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}
                                </div>

                                <button
                                    type="button"
                                    className="btn-ghost btn-sm mb-1 !px-2 text-red-600"
                                    onClick={() => setForm({
                                        ...form,
                                        items: form.items.filter((_, i) => i !== index)
                                    })}
                                >
                                    <FiTrash2 className="h-4 w-4" />
                                </button>
                            </div>
                        );
                    })}

                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => setForm({
                            ...form,
                            items: [...form.items, { stockItemId: '', quantity: '', unitPrice: '', taxPercent: 0 }]
                        })}
                    >
                        <FiPlus className="h-3.5 w-3.5" /> Add a line
                    </button>
                </div>

                <div className="grid gap-3 lg:grid-cols-[1fr_18rem]">
                    <Field label="Remarks">
                        <Textarea
                            rows={2}
                            value={form.remarks}
                            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        />
                    </Field>

                    <div className="space-y-2">
                        <Field label="Freight">
                            <NumberInput
                                value={form.freightAmount}
                                onChange={(e) => setForm({ ...form, freightAmount: e.target.value })}
                            />
                        </Field>
                        <Field label="Paid now" hint="The rest is added to the supplier's balance.">
                            <NumberInput
                                value={form.paidAmount}
                                onChange={(e) => setForm({ ...form, paidAmount: e.target.value })}
                            />
                        </Field>

                        <dl className="rounded-lg bg-slate-50 p-3 text-sm">
                            <div className="flex justify-between"><dt>Sub total</dt><dd className="tabular">{money(subTotal)}</dd></div>
                            <div className="flex justify-between"><dt>Tax</dt><dd className="tabular">{money(taxTotal)}</dd></div>
                            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-semibold">
                                <dt>Total</dt><dd className="tabular">{money(total)}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PurchasesPage;
